import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Search, X, ShoppingBag } from "lucide-react";

/**
 * <MarketplaceGearPicker value={[id, ...]} onChange={(arr) => …} />
 * Type to search marketplace listings, click to attach. Removes via X chip.
 */
export default function MarketplaceGearPicker({ value, onChange }) {
    const [linked, setLinked] = useState([]);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const debounceRef = useRef(null);

    // Resolve linked IDs → listing details (one shot per id)
    useEffect(() => {
        if (!value?.length) { setLinked([]); return; }
        let cancel = false;
        (async () => {
            try {
                // No batch endpoint — search-fetch each. Keep small.
                const all = await Promise.all(value.map((id) => api.get(`/marketplace/${id}`).then((r) => r.data).catch(() => null)));
                if (!cancel) setLinked(all.filter(Boolean));
            } catch { /* ignore */ }
        })();
        return () => { cancel = true; };
    }, [value]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        if (!query.trim()) { setResults([]); return; }
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get("/marketplace", { params: { q: query, limit: 8 } });
                setResults(data || []);
            } catch { setResults([]); }
        }, 250);
        return () => clearTimeout(debounceRef.current);
    }, [query]);

    const attach = (listing) => {
        if (!value.includes(listing.id)) {
            onChange([...value, listing.id]);
        }
        setQuery("");
        setResults([]);
    };

    return (
        <div className="space-y-2" data-testid="marketplace-picker">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                <input
                    data-testid="marketplace-picker-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search Marketplace listings to link…"
                    className="w-full pl-10 pr-3 py-2 rounded-md bg-snowcap border border-ridge-line text-sm focus:outline-none focus:border-ember-orange"
                />
                {results.length > 0 && (
                    <ul className="absolute z-10 left-0 right-0 mt-1 bg-snowcap border border-ridge-line rounded-md shadow-lg max-h-64 overflow-y-auto">
                        {results.map((r) => (
                            <li key={r.id}>
                                <button
                                    type="button"
                                    data-testid={`marketplace-picker-result-${r.id}`}
                                    onClick={() => attach(r)}
                                    className="w-full flex items-center gap-2 p-2 hover:bg-alpine-mist text-left transition-colors"
                                >
                                    {r.photos?.[0] && <img src={r.photos[0]} alt="" className="w-8 h-8 object-cover rounded shrink-0" />}
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm font-bold text-midnight-pine line-clamp-1">{r.title}</span>
                                        <span className="text-[10px] text-slate-gray uppercase tracking-widest">${r.price} · {r.category || "gear"}</span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {linked.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="marketplace-picker-linked">
                    {linked.map((l) => (
                        <span key={l.id} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full bg-trail-tan/60 text-midnight-pine text-xs font-bold">
                            <ShoppingBag className="w-3 h-3" />
                            <Link to={`/marketplace/${l.id}`} className="hover:underline line-clamp-1 max-w-[160px]">{l.title}</Link>
                            <button
                                type="button"
                                data-testid={`marketplace-picker-remove-${l.id}`}
                                onClick={() => onChange(value.filter((v) => v !== l.id))}
                                className="w-4 h-4 rounded-full hover:bg-midnight-pine/10 flex items-center justify-center"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * <MarketplaceGearGrid ids={[...]} /> — read-only render for public profile.
 */
export function MarketplaceGearGrid({ ids }) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        if (!ids?.length) { setItems([]); return; }
        let cancel = false;
        (async () => {
            try {
                const all = await Promise.all(ids.map((id) => api.get(`/marketplace/${id}`).then((r) => r.data).catch(() => null)));
                if (!cancel) setItems(all.filter(Boolean));
            } catch { /* ignore */ }
        })();
        return () => { cancel = true; };
    }, [ids]);
    if (!items.length) return null;
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="marketplace-gear-grid">
            {items.map((l) => (
                <Link
                    key={l.id}
                    to={`/marketplace/${l.id}`}
                    data-testid={`marketplace-gear-card-${l.id}`}
                    className="bg-snowcap border border-ridge-line rounded-md overflow-hidden hover:border-ember-orange transition-all group"
                >
                    {l.photos?.[0] && <img src={l.photos[0]} alt="" className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300" />}
                    <div className="p-2">
                        <div className="font-bold text-xs text-midnight-pine line-clamp-1">{l.title}</div>
                        <div className="flex items-center justify-between mt-0.5">
                            <span className="text-[10px] text-slate-gray uppercase tracking-widest">{l.category || "gear"}</span>
                            <span className="text-xs font-bold text-ember-orange">${l.price}</span>
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
}
